import { Query } from '@feathersjs/feathers';
import { useCallback } from 'react';
import { GroupBase, OptionsOrGroups } from 'react-select';

import TyAsyncSelect, { TyAsyncSelectProps } from './TyAsyncSelect';
import feathersClient from '../../apiClient';
import { getPaginatedResponse } from '../../apiClient/utils';
import { CompanyData } from '../../types/TymbeApi';

type ProvidedProps = 'loadOptions' | 'getOptionLabel' | 'getOptionValue';

type InternalProps<Fields extends object> =
  TyAsyncSelectProps<Fields, CompanyData, GroupBase<CompanyData>, void, boolean>;
type TySelectCompanyProps<Fields extends object> =
  Omit<InternalProps<Fields>, ProvidedProps>;

const loadCompanyOptions = (
  defaultOptionsCount?: number,
) => async (
  search: string,
  options: OptionsOrGroups<CompanyData, GroupBase<CompanyData>>,
) => {
  const query: Query = {
    $skip: options.length - (defaultOptionsCount || 0),
  };

  const iLike = { $ilike: `%${search}%` };
  if (search !== '' && !Number.isNaN(Number(search))) {
    query.id = Number(search);
  } else {
    query.$or = [{ name: iLike }, { display_name: iLike }];
  }

  let resp = await feathersClient.service('company').find({
    query,
  });
  resp = getPaginatedResponse(resp);

  return {
    options: resp.data,
    hasMore: resp.total > options.length + resp.data.length,
  };
};

const TySelectCompany = <Fields extends object>({
  debounceTimeout = 1000,
  defaultOptions = [],
  ...props
}: TySelectCompanyProps<Fields>) => {
  const defaultOptionsCount = Array.isArray(defaultOptions) ? defaultOptions.length : 0;
  const loadOptions = useCallback(
    () => loadCompanyOptions(defaultOptionsCount),
    [defaultOptionsCount],
  );

  return (
    <TyAsyncSelect
      {...props}
      debounceTimeout={debounceTimeout}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions()}
      getOptionLabel={(option) => `[${option.id}] ${option.name}`}
      getOptionValue={(option) => String(option.id)}
    />
  );
};

export default TySelectCompany as typeof TySelectCompany;
